import { Globe, Lock, Shuffle, Check, Minus } from "lucide-react";
import { Reveal } from "./Reveal";

const modes = [
  {
    num: "01",
    icon: Globe,
    title: "Pública",
    badge: "Acceso abierto",
    desc: "Cualquier persona con el enlace puede ver la invitación completa y confirmar asistencia. Ideal para celebraciones familiares y eventos abiertos.",
    example: "Ejemplo: un cumpleaños de 15 donde compartís el enlace libremente.",
  },
  {
    num: "02",
    icon: Lock,
    title: "Privada",
    badge: "Lista controlada",
    desc: "Cada invitado valida su teléfono: si el número está registrado en tu lista, accede a su invitación con sus pases y su mesa.",
    example: "Ejemplo: un evento empresarial donde solo ingresa quien está invitado.",
  },
  {
    num: "03",
    icon: Shuffle,
    title: "Mixta",
    badge: "Lo mejor de ambas",
    desc: "Portada, fecha y ubicación visibles para todos; confirmación, pases y mesa protegidos detrás de la validación del teléfono.",
    example: "Ejemplo: una boda con información abierta y confirmación controlada.",
  },
];

const comparison = [
  { label: "Ver la invitación", publica: true, privada: "Lista", mixta: true },
  { label: "Confirmar asistencia", publica: true, privada: "Lista", mixta: "Lista" },
  { label: "Ver pases asignados", publica: false, privada: "Lista", mixta: "Lista" },
  { label: "Ver mesa asignada", publica: false, privada: "Lista", mixta: "Lista" },
];

const Cell = ({ value }) => {
  if (value === true)
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
        <Check className="h-4 w-4 text-emerald-600" /> Todos
      </span>
    );
  if (value === false)
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-slate-400">
        <Minus className="h-4 w-4" /> No aplica
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
      <Lock className="h-3.5 w-3.5 text-indigo-600" /> Con validación
    </span>
  );
};

export const Differential = () => (
  <section id="diferencial" data-testid="differential-section" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-600">El diferencial Plus</p>
        <h2
          className="mt-3 max-w-2xl text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Vos decidís quién accede a la información de tu evento
        </h2>
        <p className="mt-4 max-w-2xl text-base text-slate-600">
          Un mismo enlace para todos tus invitados, sin preparar enlaces individuales. Elegí la modalidad de acceso que mejor se adapte a tu evento.
        </p>
      </Reveal>

      <div className="mt-14 space-y-0">
        {modes.map((m, i) => (
          <Reveal key={m.num} delay={i * 0.08}>
            <article
              data-testid={`access-mode-${m.title.toLowerCase()}`}
              className="group grid grid-cols-1 gap-4 border-t border-slate-200 py-8 transition-colors duration-300 hover:bg-indigo-50/40 sm:grid-cols-[80px_1fr_1.4fr] sm:gap-8 sm:px-4"
            >
              <span
                className="text-4xl font-light text-indigo-200 transition-colors duration-300 group-hover:text-indigo-400"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {m.num}
              </span>
              <div>
                <div className="flex items-center gap-3">
                  <m.icon className="h-5 w-5 text-indigo-600" />
                  <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Modalidad {m.title}
                  </h3>
                </div>
                <span className="mt-2 inline-block rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                  {m.badge}
                </span>
              </div>
              <div>
                <p className="text-base leading-relaxed text-slate-600">{m.desc}</p>
                <p className="mt-2 text-sm italic text-slate-400">{m.example}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div data-testid="access-comparison-table" className="mt-12 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full min-w-[560px] text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70">
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500">¿Quién puede…?</th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Pública</th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Privada</th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Mixta</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row) => (
                <tr key={row.label} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-4 text-sm font-medium text-slate-800">{row.label}</td>
                  <td className="px-5 py-4"><Cell value={row.publica} /></td>
                  <td className="px-5 py-4"><Cell value={row.privada} /></td>
                  <td className="px-5 py-4"><Cell value={row.mixta} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p data-testid="access-honest-note" className="mt-4 max-w-3xl text-sm text-slate-500">
          La validación comprueba que el teléfono esté registrado en tu lista de invitados. No incluye códigos de verificación (OTP) ni impide que alguien comparta su acceso: es una capa de control práctica, no una barrera absoluta.
        </p>
      </Reveal>
    </div>
  </section>
);
